<script setup lang="ts">
import type { BreadcrumbProps } from '#seo-utils/app/composables/useBreadcrumbItems'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<Pick<BreadcrumbProps, 'overrides'> & { hideMobileBack?: boolean }>()

const breadcrumbs = useBreadcrumbItems(props)

const root = useRootNavItem()

const computedBreadcrumbs = useArrayMap(breadcrumbs, (crumb, i) => i === 0 ? { ...root.value, label: 'Hjem' } : crumb)

const previousCrumb = computed(() => {
  for (let i = computedBreadcrumbs.value.length - 2; i >= 0; i--) {
    const crumb = computedBreadcrumbs.value[i]
    if (crumb?.to) {
      return crumb
    }
  }
  return null
})
</script>

<template>
  <nav class="flex justify-between items-center text-caption min-w-0" aria-label="Brødsmulesti">
    <ol class="hidden md:flex max-w-full min-w-0">
      <!-- Shrink is weighted by width × factor; a smaller factor takes a fraction of a pixel from the other crumbs, enough to show their ellipsis while the last one still has room. -->
      <li
        v-for="(crumb, index) in computedBreadcrumbs" :key="`breadcrumb-${index}`"
        class="text-carbon-400 dark:text-carbon-200 [&:not(:first-child)]:before:content-['/'] [&:not(:first-child)]:before:px-3xs min-w-0 overflow-hidden text-ellipsis whitespace-nowrap first-of-type:shrink-0 last-of-type:shrink-[1000000] last-of-type:max-w-[15rem]"
      >
        <NuxtLink
          :to="crumb.to"
          :title="crumb.label"
          class="text-caption no-underline  text-sm"
          :class="{
            'hover:underline hover:text-black dark:hover:text-white': !!crumb.to,
          }"
          exact-active-class="text-black dark:text-white"
        >
          {{ crumb.label }}
        </NuxtLink>
      </li>
    </ol>

    <ol v-if="previousCrumb && !hideMobileBack" class="flex md:hidden max-w-full min-w-0">
      <li class="min-w-0">
        <NuxtLink
          :to="previousCrumb.to"
          class="group flex min-h-11 min-w-0 items-center gap-xs rounded-lg text-primary dark:text-carbon-200 no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blurple-400 dark:focus-visible:ring-blurple-200 focus-visible:ring-offset-2"
        >
          <span
            aria-hidden="true"
            class="inline-flex size-11 shrink-0 items-center justify-center rounded-lg border border-border-default dark:border-carbon-200 group-hover:bg-white dark:group-hover:bg-blurple-400"
          >
            <Icon :icon="faArrowLeft" size="md" />
          </span>
          <span class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" :title="previousCrumb.label">{{ previousCrumb.label }}</span>
        </NuxtLink>
      </li>
    </ol>
    <slot />
  </nav>
</template>
